import { useMemo, useCallback } from "react";
import { useSession } from "next-auth/react";

/**
 * List of routes that should be restricted for platform practitioners
 * with bookingEngineEnabled === false
 */
export const RESTRICTED_ROUTES = [
  "/dashboard",
  "/bookings",
  "/my-patients",
  "/event-types",
  "/availability",
  "/my-raiqa",
  "/reports",
  "/workflows",
  "/prescriptions",
  "/invoices",
] as const;

export type RestrictedRoute = typeof RESTRICTED_ROUTES[number];

/**
 * Hook to check route restrictions for platform practitioners
 * with bookingEngineEnabled === false
 */
export const useRouteRestriction = () => {
  const { data: session, status: sessionStatus } = useSession();

  const restrictionData = useMemo(() => {
    const user = session?.user;
    
    // Check if user is a platform practitioner
    const isPlatformPractitioner = 
      user?.userCategory === "PRACTITIONER" && 
      user?.userType === "PLATFORM";
    
    // Get booking engine enabled status
    const bookingEngineEnabled = user?.bookingEngineEnabled ?? true;
    
    // User is restricted if they are a platform practitioner AND booking engine is disabled
    const isRestricted = isPlatformPractitioner && bookingEngineEnabled === false;
    
    return {
      isPlatformPractitioner,
      bookingEngineEnabled,
      isRestricted,
      restrictedRoutes: RESTRICTED_ROUTES,
    };
  }, [session?.user]);

  /**
   * Check if a specific route is restricted for the current user
   */
  const isRouteRestricted = useCallback((route: string): boolean => {
    if (!restrictionData.isRestricted) {
      return false;
    }
    
    // Check if the route starts with any restricted route
    return RESTRICTED_ROUTES.some((restrictedRoute) => 
      route.startsWith(restrictedRoute)
    );
  }, [restrictionData.isRestricted]);

  /**
   * Check if the current user can access a specific route
   */
  const canAccessRoute = useCallback((route: string): boolean => {
    return !isRouteRestricted(route);
  }, [isRouteRestricted]);

  return {
    ...restrictionData,
    isRouteRestricted,
    canAccessRoute,
    isLoading: sessionStatus === "loading",
  };
};

